import { z } from 'zod';
import {
  dnsDomainsSchema,
  dnsResolverAddrsSchema,
  lighthouseIntervalSchema,
  lighthouseServeDNSOffSchema,
  lighthouseServeDNSOnSchema,
  listenBatchSchema,
  listenHostSchema,
  listenReadBufferSchema,
  listenUDPOffloadsSchema,
  listenWriteBufferSchema,
  localAllowListSchema,
  loggingFormatSchema,
  loggingLevelSchema,
  loggingTimestampFormatSchema,
  preferredRangesSchema,
  punchyDelaySchema,
  punchyRespondDelaySchema,
  remoteAllowListSchema,
  routinesSchema,
  staticMapNetworkSchema,
  statsHostSchema,
  statsIntervalSchema,
  statsLighthouseMetricsSchema,
  statsListenSchema,
  statsMessageMetricsSchema,
  statsNamespaceSchema,
  statsPathSchema,
  statsPrefixSchema,
  statsSubsystemSchema,
  tunDevSchema,
  tunDisabledSchema,
  tunMTUSchema,
  tunTxQueueSchema,
  tunUseOffloadsSchema,
} from '#schemas/config-override/index.ts';
import { IPAndRequiredPortListSchema } from '#schemas/general/ip-address-and-required-port.ts';

/** stats options shared by prometheus and graphite */
const generalStatsOptionsSchema = z.object({
  interval: statsIntervalSchema,
  lighthouse_metrics: statsLighthouseMetricsSchema,
  message_metrics: statsMessageMetricsSchema,
});

/** {@link configOverridesSchema} is a useful form schema for validating config overrides set on the HostForm and for adding config overrides in the TagForm */
export const configOverridesSchema = z.object({
  definednet: z.object({
    dns: z.object({
      resolver_addrs: dnsResolverAddrsSchema,
      match_domains: dnsDomainsSchema,
      search_domains: dnsDomainsSchema,
    }),
  }),
  lighthouse: z.discriminatedUnion('serve_dns', [
    z.object({
      // TODO: explore validating the items of this list in the schema as well
      // Currently we basically validate the list via the schema, and do timely validation in the modals
      remote_allow_list: remoteAllowListSchema,
      local_allow_list: localAllowListSchema,
      serve_dns: z.literal('On'),
      dns: lighthouseServeDNSOnSchema,
      advertise_addrs: IPAndRequiredPortListSchema,
      interval: lighthouseIntervalSchema,
    }),
    z.object({
      remote_allow_list: remoteAllowListSchema,
      local_allow_list: localAllowListSchema,
      serve_dns: z.literal('Off'),
      dns: lighthouseServeDNSOffSchema,
      advertise_addrs: IPAndRequiredPortListSchema,
      interval: lighthouseIntervalSchema,
    }),
    // Unfortunately we can't combine "off" and "", so this is duplicated
    z.object({
      remote_allow_list: remoteAllowListSchema,
      local_allow_list: localAllowListSchema,
      serve_dns: z.literal(''),
      dns: lighthouseServeDNSOffSchema,
      advertise_addrs: IPAndRequiredPortListSchema,
      interval: lighthouseIntervalSchema,
    }),
  ]),
  listen: z.object({
    batch: listenBatchSchema,
    read_buffer: listenReadBufferSchema,
    write_buffer: listenWriteBufferSchema,
    host: listenHostSchema,
    udp_offloads: listenUDPOffloadsSchema,
  }),
  tun: z.object({
    dev: tunDevSchema,
    disabled: tunDisabledSchema,
    tx_queue: tunTxQueueSchema,
    mtu: tunMTUSchema,
    use_offloads: tunUseOffloadsSchema,
  }),
  logging: z.object({
    format: loggingFormatSchema,
    level: loggingLevelSchema,
    timestamp_format: loggingTimestampFormatSchema,
  }),
  stats: z.discriminatedUnion('type', [
    z.object({
      type: z.literal(''),
      /** Only validate that we have string values for the stats object when we don't have a type set */
      interval: z.nullable(z.string()),
      lighthouse_metrics: statsLighthouseMetricsSchema,
      message_metrics: statsMessageMetricsSchema,
      listen: z.string(),
      path: z.string(),
      namespace: z.string(),
      subsystem: z.string(),
      host: z.string(),
      prefix: z.string(),
    }),
    z
      .object({
        type: z.literal('prometheus'),
        listen: statsListenSchema,
        path: statsPathSchema,
        namespace: statsNamespaceSchema,
        subsystem: statsSubsystemSchema,
        /** We are generous when validating Graphite values when in Prometheus, these will be dropped when serialized */
        host: z.string(),
        prefix: z.string(),
      })
      .merge(generalStatsOptionsSchema),
    z
      .object({
        type: z.literal('graphite'),
        host: statsHostSchema,
        prefix: statsPrefixSchema,
        /** We are generous when validating Prometheus values when in Graphite, these will be dropped when serialized */
        listen: z.string(),
        path: z.string(),
        namespace: z.string(),
        subsystem: z.string(),
      })
      .merge(generalStatsOptionsSchema),
  ]),
  static_map: z.object({
    network: staticMapNetworkSchema,
  }),
  punchy: z.object({
    delay: punchyDelaySchema,
    respond_delay: punchyRespondDelaySchema,
  }),
  preferred_ranges: preferredRangesSchema,
  routines: routinesSchema,
});
